import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {OAuthService} from 'angular-oauth2-oidc';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../environments/environment';
import {PortalIdpService} from './portal-idp.service';
import {BannerService} from '../banner/banner.service';

const STORAGE_KEY = 'federated_api_token';
// Re-exchange this long before the CS token expires, so a request never leaves with a token that
// dies in flight.
const REFRESH_SKEW_MS = 60_000;

interface FederatedToken {
  // The above id_token this CS token was exchanged for. A silent refresh brings a new id_token --
  // possibly with different cognito:groups -- so a mismatch means exchange again.
  source_id_token: string;
  access_token: string;
}

/**
 * The bearer token for content-platform backends (AppSync, VTK).
 *
 * castlabs users send their castlabs SSO access token, as before. above users log in at above's
 * Cognito, but their token never reaches a backend: it is traded at the federate endpoint for a
 * castlabs/CS token, and that is what gets sent. The federate Lambda provisions the user's janus
 * memberships from their above groups, so the backends authorize them like any janus user.
 *
 * The above token is still what `OAuthService` holds -- login, silent refresh, logout and
 * Cognito's own user API (MFA setup) stay on it.
 */
@Injectable({providedIn: 'root'})
export class ApiTokenService {

  private pending: Promise<string> | null = null;
  // Every query retries the exchange, so a broken federate would otherwise stack one banner per query.
  private bannerShown = false;

  constructor(private oAuthService: OAuthService, private portalIdp: PortalIdpService, private http: HttpClient,
              private banner: BannerService) {
    oAuthService.events.subscribe(event => {
      if (event.type === 'logout') {
        this.forget();
      }
    });
  }

  accessToken(): Promise<string> {
    if (this.portalIdp.getIdpName() !== 'above') {
      return Promise.resolve(this.oAuthService.getAccessToken());
    }
    const idToken = this.oAuthService.getIdToken();
    if (!idToken) {
      // Not logged in. Send nothing rather than the above token.
      return Promise.resolve('');
    }
    const stored = this.read();
    if (stored && stored.source_id_token === idToken && expiresAt(stored.access_token) - Date.now() > REFRESH_SKEW_MS) {
      return Promise.resolve(stored.access_token);
    }
    // One exchange at a time: the first page load fires several queries at once.
    if (!this.pending) {
      this.pending = this.exchange(idToken).finally(() => this.pending = null);
    }
    return this.pending;
  }

  private async exchange(idToken: string): Promise<string> {
    try {
      const res = await firstValueFrom(
        this.http.post<{ access_token: string }>(environment.federate_url, {id_token: idToken})
      );
      this.write({source_id_token: idToken, access_token: res.access_token});
      return res.access_token;
    } catch (e) {
      const message = e instanceof HttpErrorResponse ? (e.error?.error ?? e.message) : String(e);
      console.error('Token exchange at federate failed:', message);
      this.showFailure(e instanceof HttpErrorResponse && e.status === 0
        // The browser hides the cause of a network/CORS failure from the page; only the console has it.
        ? 'the federate service could not be reached (network or CORS error, see the browser console)'
        : message);
      throw new Error(`Token exchange failed: ${message}`);
    }
  }

  // Without this the failure is invisible: every query fails, the pages just render empty.
  private showFailure(reason: string): void {
    if (this.bannerShown) {
      return;
    }
    this.bannerShown = true;
    this.banner.open(`Could not get API credentials: ${escapeHtml(reason)}. Nothing will load until this is fixed.`,
      ['Reload', 'Dismiss']).subscribe(action => {
      this.bannerShown = false;
      if (action === 0) {
        window.location.reload();
      }
    });
  }

  private read(): FederatedToken | null {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    } catch {
      return null;
    }
  }

  private write(token: FederatedToken): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(token));
  }

  private forget(): void {
    localStorage.removeItem(STORAGE_KEY);
  }
}

/** Epoch millis from the JWT's `exp` claim; 0 if unreadable, which forces a re-exchange. */
function expiresAt(jwt: string): number {
  try {
    const payload = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload)).exp * 1000;
  } catch {
    return 0;
  }
}

/** The banner renders its message as HTML; the reason comes from the server. */
function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]!));
}
